import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { LoadingSpinnerComponent, ProjectStepperService } from '@shared';
import { catColors } from '@core/constants/category-colors.constants';
import { CATEGORY } from '@core';
import { TranslatePipe } from '@ngx-translate/core';

/**
 * Lets the user choose which detection categories should run for the project.
 */
@Component({
  selector: 'app-step-detection-scope',
  imports: [LoadingSpinnerComponent, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './step-detection-scope.component.html',
  styleUrl: './step-detection-scope.component.css',
})
export class StepDetectionScopeComponent {
  /** Shared stepper state containing category selection and save status. */
  readonly stepper = inject(ProjectStepperService);

  /** Category color map used by detection cards. */
  readonly catColors = catColors;

  /** Image asset paths shown for each detection scope card. */
  readonly categoryImages: Record<string, string> = {
    [CATEGORY.FLOORING.id]:       'images/scopes/tile.png',
    [CATEGORY.DRYWALL.id]:        'images/scopes/drywall.png',
    [CATEGORY.DOORS_WINDOWS.id]:  'images/scopes/wooden-door.png',
    [CATEGORY.INTERIOR_PAINT.id]: 'images/scopes/roller-brush.png',
    [CATEGORY.FIXTURES.id]:       'images/scopes/water-tap.png',
    [CATEGORY.HVAC.id]:           'images/scopes/mechanic.png',
    [CATEGORY.COLUMNS.id]:        'images/scopes/columns.png',
    [CATEGORY.STEEL_BEAMS.id]:    'images/scopes/steel-beams.png',
  };

  iconFor(category: { id: string; icon?: string }): string | null {
    return this.categoryImages[category.id] ?? category.icon ?? null;
  }

  categoryLabelKey(id: string): string {
    return `PROJECT_STEPPER.CATEGORIES.${this.categoryKey(id)}.LABEL`;
  }

  categoryDescriptionKey(id: string): string {
    return `PROJECT_STEPPER.CATEGORIES.${this.categoryKey(id)}.DESCRIPTION`;
  }

  private categoryKey(id: string): string {
    const keys: Record<string, string> = {
      [CATEGORY.FLOORING.id]:       'FLOORING',
      [CATEGORY.DRYWALL.id]:        'DRYWALL',
      [CATEGORY.FIXTURES.id]:       'FIXTURES',
      [CATEGORY.DOORS_WINDOWS.id]:  'DOORS_WINDOWS',
      [CATEGORY.INTERIOR_PAINT.id]: 'INTERIOR_PAINT',
      [CATEGORY.HVAC.id]:           'HVAC',
      [CATEGORY.COLUMNS.id]:        'COLUMNS',
      [CATEGORY.STEEL_BEAMS.id]:    'STEEL_BEAMS',
    };
    return keys[id] ?? 'UNKNOWN';
  }

  /**
   * Returns whether a category id is selected.
   *
   * @param id Detection category id.
   */
  isSelected(id: string): boolean {
    return this.stepper.selectedCategories().includes(id);
  }

  /**
   * Returns whether a category card should be disabled by the max-selection rule.
   *
   * @param id Detection category id.
   */
  isDisabled(id: string): boolean {
    return (
      !this.isSelected(id) &&
      this.stepper.selectedCategories().length >= this.stepper.maxDetectionScopeCategories
    );
  }
}
